import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import ArrowRightIcon from "@heroicons/react/20/solid/ArrowRightIcon";

// Local
import { useStiltNav } from "../../navigation";
import { DbRead, DbWrite } from "../../database";
import { NotFoundError } from "../../error-classes";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { useState } from "react";
import AlertV2 from "../../components/AlertV2";
import CallsEditLayout, { ListItem } from "./CallsEditLayout";
import BaseInputSelect from "../../components/BaseInputSelect";
import { CallStatusEnum, type CallStatus } from "../../models/calls";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import { useIncomingCallsStore } from "../../store/incoming-calls";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";

interface Props {
  siteKey: string;
  children?: React.ReactNode;
}

const CallPropertiesToExclude = [
  "callToken",
  "id",
  "refPath",
  "callSid",
  "accountSid",
  "apiVersion",
  "callSide",
  "stirVerstat",
] as const;

export default function CallsEditContainer(props: Props): JSX.Element {
  const params = useParams<{ id: string }>();
  const nav = useStiltNav();
  const [notFound, setNotFound] = useState(false);

  const setCurrentCall = useIncomingCallsStore((state) => state.setCurrentCall);
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const callId = params.id;

  const callQuery = useQuery(
    ["call", callId],
    () => {
      if (!callId) {
        // Should never happen if the route is set up correctly
        throw new Error("No call ID provided");
      }
      return DbRead.calls.get({ siteKey: props.siteKey, callDocId: callId });
    },
    {
      enabled: !!callId,
      refetchOnWindowFocus: false,
      retry: 2,
      onError: (error) => {
        if (error instanceof NotFoundError) {
          setNotFound(true);
        }
      },
      onSuccess: (_data) => {
        setNotFound(false);
      },
    },
  );
  const updateStatusMutation = useMutation(
    async (status: string) => {
      if (!callId) {
        throw new Error("No call ID provided");
      }
      // Invalid status, skip update
      if (!Object.keys(CallStatusEnum).includes(status)) {
        throw new Error("Invalid status");
      }
      return DbWrite.calls.updateCallStatus({
        siteKey: props.siteKey,
        callDocID: callId,
        status: status as CallStatus,
      });
    },
    {
      onSuccess: () => {
        addToastMessage({
          type: "success",
          message: "Call status updated",
          id: createToastMessageID(),
          dialog: false,
        });
      },
      onError: (error) => {
        console.error(error);
        addToastMessage({
          type: "error",
          message: "Failed to update call status",
          id: createToastMessageID(),
          dialog: false,
        });
      },
    },
  );

  if (!callId) {
    nav.notFoundPage();
    return <></>;
  }

  if (notFound) {
    return (
      <CenteredLayout>
        <AlertV2
          title={"Call not found"}
          variant={"error"}
          message={
            "Unable to find the call document. Please try again or contact support."
          }
        />
      </CenteredLayout>
    );
  }

  if (callQuery.isLoading) {
    return (
      <CenteredLayout>
        <LoadingClipboardAnimation />
      </CenteredLayout>
    );
  }

  if (callQuery.isError) {
    return <div>Error: Something went wrong</div>;
  }

  return (
    <CallsEditLayout title={"Edit Call"}>
      {{
        listItems:
          callQuery.data &&
          Object.entries(callQuery.data).map(([fieldName, fieldValue]) => {
            if (CallPropertiesToExclude.includes(fieldName as any)) return null;
            // if (fieldName === "customerID") {
            //   console.log("1", fieldName);
            // }
            if (
              !(
                typeof fieldValue === "string" ||
                typeof fieldValue === "number" ||
                fieldValue === null
              )
            ) {
              return null;
            }
            // if (fieldName === "customerID") {
            //   console.log("2", fieldName);
            // }

            if (fieldName === "callStatus") {
              return (
                <ListItem
                  key={fieldName}
                  label={fieldName}
                  value={
                    <BaseInputSelect
                      id={"call-status-select"}
                      inputName={"call-status-select"}
                      admin={false}
                      defaultValue={fieldValue}
                    >
                      <option value="">No status set</option>
                      {Object.entries(CallStatusEnum).map(
                        ([statusValue, statusDisplay]) => (
                          <option key={statusValue} value={statusValue}>
                            {statusDisplay}
                          </option>
                        ),
                      )}
                    </BaseInputSelect>
                  }
                  actions={
                    <BaseButtonSecondary
                      disabled={updateStatusMutation.isLoading}
                      onClick={() => {
                        const el =
                          document.getElementById("call-status-select");
                        if (!el) return;
                        const status = (el as HTMLSelectElement).value;
                        updateStatusMutation.mutate(status);
                      }}
                    >
                      Save
                    </BaseButtonSecondary>
                  }
                />
              );
            }

            if (fieldName === "customerID") {
              return (
                <ListItem
                  key={fieldName}
                  label={fieldName}
                  value={fieldValue}
                  actions={
                    <BaseButtonSecondary
                      onClick={() => {
                        const currentCall = callQuery.data;
                        setCurrentCall(currentCall);
                        nav.customerSearchPage({
                          query:
                            (currentCall?.direction === "inbound"
                              ? currentCall.caller
                              : currentCall?.to) ?? "",
                        });
                      }}
                    >
                      Change Customer
                      <span className="ml-2">
                        <ArrowRightIcon className="h-4 w-4" />
                      </span>
                    </BaseButtonSecondary>
                  }
                />
              );
            }

            return (
              <ListItem key={fieldName} label={fieldName} value={fieldValue} />
            );
          }),
      }}
    </CallsEditLayout>
  );
}

function CenteredLayout({ children }: { children: React.ReactNode }) {
  return <div className="grid h-full place-items-center">{children}</div>;
}
